/**
 * Robot manufacturer context provider
 * Provides manufacturer-specific data and script generators to child components
 */

import { isEmpty } from 'lodash';
import { ReactNode, createContext, useContext, useMemo } from 'react';
import { robotRegistryService } from '../../robot/robot-registry.service';
import {
  ManufacturerNameArg,
  ManufacturerPlugin,
  RobotKinematics,
  RobotManufacturer,
} from '../../robot/robot.types';
import { getManufacturerByName } from '../../robot/robot.utils';
import { logger } from '../../util/logger';
import { useMachineContext } from './machine-context-provider';

export interface RobotManufacturerContextValue<ManufacturerModel = string> {
  plugin?: ManufacturerPlugin<ManufacturerModel>;
  manufacturer?: RobotManufacturer;
  manufacturerDisplayName?: ManufacturerNameArg;
  model?: ManufacturerModel;
  kinematics?: RobotKinematics;
}

export const RobotManufacturerContext =
  createContext<RobotManufacturerContextValue>({});

export interface RobotManufacturerProviderProps {
  children: ReactNode;
}

export const RobotManufacturerProvider = (
  props: RobotManufacturerProviderProps
): ReactNode => {
  const { children } = props;
  const { machine } = useMachineContext();

  const contextValue = useMemo<RobotManufacturerContextValue<string>>(() => {
    const runtimeInfo = machine.state?.runtimeInfo;
    const machineModel: string | undefined = runtimeInfo?.model;
    const machineManufacturer: string | undefined = runtimeInfo?.manufacturer;

    if (isEmpty(machineModel) || isEmpty(machineManufacturer)) {
      logger.error('[robot-mfr-provider] machine runtime info incomplete', {
        machine,
      });
      return {};
    }

    const manufacturer = getManufacturerByName(machineManufacturer);

    if (!manufacturer) {
      logger.error('[robot-mfr-provider] unknown manufacturer', {
        machine,
        machineManufacturer,
      });
      return {};
    }

    const robotPlugin = robotRegistryService.getPlugin(manufacturer);

    if (!robotPlugin) {
      logger.error('[robot-mfr-provider] no robot plugin found for machine', {
        machine,
      });
      return {
        manufacturer,
      };
    }

    const model = robotPlugin.getModelByNumber(machineModel);

    if (!model) {
      logger.error('[robot-mfr-provider] unknown model number', {
        machine,
        machineModel,
      });
      return {
        plugin: robotPlugin,
        manufacturer,
      };
    }

    const manufacturerDisplayName = robotPlugin.getManufacturerDisplayName();
    const kinematics = robotPlugin.getKinematics(model);

    return {
      plugin: robotPlugin,
      manufacturer,
      manufacturerDisplayName,
      model,
      kinematics,
    };
  }, [machine]);

  return (
    <RobotManufacturerContext.Provider value={contextValue}>
      {children}
    </RobotManufacturerContext.Provider>
  );
};

export const useRobotManufacturerContext =
  (): RobotManufacturerContextValue => {
    const context = useContext(RobotManufacturerContext);

    return context;
  };
